import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { scoreline } from "../results.js";

/**
 * The arbiter's round console.
 *
 * One page to pair a round, start it, watch the boards and intervene. It
 * polls rather than holding a socket: an arbiter watching 200 boards needs a
 * periodic summary, not 200 live streams, and the board view is a table of
 * numbers rather than positions.
 */

interface Board {
  pairingId: number;
  boardNo: number;
  white: { userId: number | null; nickname: string | null };
  black: { userId: number | null; nickname: string | null };
  gameId: number | null;
  gamePublicId: string | null;
  gameState: string | null;
  result: string | null;
  resultSource: string | null;
  clock: { whiteMs: number; blackMs: number } | null;
  turn: "w" | "b" | null;
  whiteConnected: boolean | null;
  blackConnected: boolean | null;
}

interface Round {
  id: number;
  number: number;
  state: string;
  startedAt: string | null;
}

const REFRESH_MS = 3000;

function mmss(ms: number | undefined): string {
  if (ms === undefined) return "—";
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export function Rounds() {
  const { slug } = useParams();
  const [rounds, setRounds] = useState<Round[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [boards, setBoards] = useState<Board[]>([]);
  const [round, setRound] = useState<Round | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const loadRounds = useCallback(async () => {
    const res = await fetch(`/api/tournaments/${slug}/rounds`);
    if (!res.ok) return;
    const data = await res.json();
    setRounds(data.rounds);
    // Follow the latest round unless the arbiter has chosen another.
    setSelected((cur) => cur ?? data.rounds[data.rounds.length - 1]?.id ?? null);
  }, [slug]);

  const loadBoards = useCallback(async () => {
    if (selected === null) return;
    const res = await fetch(`/api/admin/rounds/${selected}/boards`);
    if (!res.ok) {
      setNotice(res.status === 403 ? "Arbiter access required." : null);
      return;
    }
    const data = await res.json();
    setBoards(data.boards);
    setRound(data.round);
  }, [selected]);

  useEffect(() => {
    void loadRounds();
  }, [loadRounds]);

  useEffect(() => {
    void loadBoards();
    const t = setInterval(() => void loadBoards(), REFRESH_MS);
    return () => clearInterval(t);
  }, [loadBoards]);

  async function post(url: string, body?: unknown): Promise<void> {
    setBusy(true);
    setNotice(null);
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body ?? {}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setNotice(data.message ?? data.error ?? "That did not work");
      }
      await loadRounds();
      await loadBoards();
    } finally {
      setBusy(false);
    }
  }

  const unresolved = boards.filter(
    (b) => b.result === null && b.resultSource === null
  ).length;

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      <p style={{ marginBottom: 8 }}>
        <Link to={`/t/${slug}`}>← {slug}</Link>
      </p>
      <h1 style={{ marginTop: 0 }}>Rounds</h1>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        {rounds.map((r) => (
          <button
            key={r.id}
            onClick={() => setSelected(r.id)}
            style={{
              padding: "6px 14px",
              borderRadius: 6,
              border: "1px solid #ccc",
              background: r.id === selected ? "#00695c" : "#fff",
              color: r.id === selected ? "#fff" : "#333",
              cursor: "pointer",
            }}
          >
            Round {r.number}
            <span style={{ opacity: 0.7, fontSize: 12 }}> · {r.state}</span>
          </button>
        ))}
        <button
          onClick={() => void post(`/api/admin/tournaments/${slug}/rounds`)}
          disabled={busy}
          style={{
            padding: "6px 14px",
            borderRadius: 6,
            border: "1px solid #00695c",
            background: "#fff",
            color: "#00695c",
            cursor: "pointer",
          }}
        >
          + Pair next round
        </button>
      </div>

      {notice && (
        <p
          style={{
            background: "#fff3e0",
            border: "1px solid #ffb74d",
            padding: "8px 12px",
            borderRadius: 6,
          }}
        >
          {notice}
        </p>
      )}

      {round && (
        <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center" }}>
          <strong>Round {round.number}</strong>
          <span style={{ color: "#666" }}>{round.state}</span>
          <span style={{ color: "#666" }}>
            {boards.length} board{boards.length === 1 ? "" : "s"}, {unresolved} unresolved
          </span>
          <span style={{ flex: 1 }} />
          {(round.state === "ready" || round.state === "pending") && (
            <>
              <button
                onClick={() => void post(`/api/admin/rounds/${round.id}/start`)}
                disabled={busy}
                style={primary}
              >
                Start round
              </button>
              <button
                onClick={() => {
                  if (!confirm(`Discard round ${round.number} and re-pair?`)) return;
                  void fetch(`/api/admin/rounds/${round.id}`, { method: "DELETE" }).then(
                    () => {
                      setSelected(null);
                      void loadRounds();
                    }
                  );
                }}
                disabled={busy}
                style={secondary}
              >
                Discard
              </button>
            </>
          )}
          {round.state === "running" && (
            <button
              onClick={() => void post(`/api/admin/rounds/${round.id}/finish`)}
              disabled={busy}
              style={primary}
            >
              Finish round
            </button>
          )}
        </div>
      )}

      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
        <thead>
          <tr style={{ textAlign: "left", borderBottom: "2px solid #ddd" }}>
            <th style={th}>#</th>
            <th style={th}>White</th>
            <th style={th}>Black</th>
            <th style={th}>Clock</th>
            <th style={th}>State</th>
            <th style={th}>Result</th>
            <th style={th}>Arbiter</th>
          </tr>
        </thead>
        <tbody>
          {boards.map((b) => (
            <tr key={b.pairingId} style={{ borderBottom: "1px solid #eee" }}>
              <td style={td}>{b.boardNo}</td>
              <td style={td}>
                <Presence on={b.whiteConnected} />
                {b.white.nickname ?? "—"}
                {b.turn === "w" && b.gameState === "running" && " ●"}
              </td>
              <td style={td}>
                {b.black.nickname === null ? (
                  <em style={{ color: "#888" }}>bye</em>
                ) : (
                  <>
                    <Presence on={b.blackConnected} />
                    {b.black.nickname}
                    {b.turn === "b" && b.gameState === "running" && " ●"}
                  </>
                )}
              </td>
              <td style={{ ...td, fontVariantNumeric: "tabular-nums" }}>
                {b.clock ? `${mmss(b.clock.whiteMs)} / ${mmss(b.clock.blackMs)}` : "—"}
              </td>
              <td style={td}>{b.gameState ?? "—"}</td>
              <td style={td}>
                {b.result ?? (b.resultSource === "forfeit" ? scoreline(null) : "—")}
                {b.resultSource && b.resultSource !== "play" && (
                  <span style={{ color: "#888", fontSize: 12 }}> ({b.resultSource})</span>
                )}
              </td>
              <td style={td}>
                {b.gameId !== null && b.result === null && (
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {b.gameState === "running" && (
                      <Mini onClick={() => void post(`/api/admin/games/${b.gameId}/pause`)}>
                        pause
                      </Mini>
                    )}
                    {b.gameState === "paused" && (
                      <Mini onClick={() => void post(`/api/admin/games/${b.gameId}/resume`)}>
                        resume
                      </Mini>
                    )}
                    <Mini
                      onClick={() =>
                        void post(`/api/admin/games/${b.gameId}/time`, {
                          side: "w",
                          ms: 60_000,
                          reason: "arbiter compensation",
                        })
                      }
                    >
                      +1m W
                    </Mini>
                    <Mini
                      onClick={() =>
                        void post(`/api/admin/games/${b.gameId}/time`, {
                          side: "b",
                          ms: 60_000,
                          reason: "arbiter compensation",
                        })
                      }
                    >
                      +1m B
                    </Mini>
                    <Mini onClick={() => void decide(b, "white")}>2-0</Mini>
                    <Mini onClick={() => void decide(b, "draw")}>1-1</Mini>
                    <Mini onClick={() => void decide(b, "black")}>0-2</Mini>
                  </div>
                )}
                {b.gamePublicId && (
                  <Link
                    to={`/g/${b.gamePublicId}`}
                    style={{ fontSize: 12, marginLeft: 4 }}
                  >
                    watch
                  </Link>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {boards.length === 0 && (
        <p style={{ color: "#666" }}>
          No boards yet. Pair a round to begin.
        </p>
      )}
    </div>
  );

  async function decide(b: Board, result: "white" | "draw" | "black"): Promise<void> {
    // An arbiter decision overrides a played result, so it is confirmed:
    // there is no undo, and it changes the crosstable immediately.
    const reason = prompt(`Reason for deciding board ${b.boardNo} as ${result}?`);
    if (reason === null) return;
    await post(`/api/admin/games/${b.gameId}/result`, {
      result,
      source: "arbiter",
      reason,
    });
  }
}

function Presence({ on }: { on: boolean | null }) {
  if (on === null) return null;
  return (
    <span
      title={on ? "connected" : "not connected"}
      style={{
        display: "inline-block",
        width: 8,
        height: 8,
        borderRadius: "50%",
        marginRight: 6,
        background: on ? "#2e7d32" : "#ccc",
      }}
    />
  );
}

function Mini({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "2px 6px",
        fontSize: 12,
        border: "1px solid #ccc",
        borderRadius: 4,
        background: "#fff",
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

const th: React.CSSProperties = { padding: "6px 8px", fontWeight: 600 };
const td: React.CSSProperties = { padding: "6px 8px" };
const primary: React.CSSProperties = {
  padding: "6px 14px",
  borderRadius: 6,
  border: "none",
  background: "#00695c",
  color: "#fff",
  cursor: "pointer",
};
const secondary: React.CSSProperties = {
  padding: "6px 14px",
  borderRadius: 6,
  border: "1px solid #ccc",
  background: "#fff",
  cursor: "pointer",
};
